import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  getGetAllowedOrganizationTagsQueryKey,
  useGetAllowedOrganizationTagsHook,
} from '@/api/trafikportalen';
import type { OrganizationAllowedBobcatTagDTO } from '@/api/trafikportalen';

/** Each organisation's allowed tags, in the order of `organizationIds`. */
export const useOrganizationsAllowedTags = (
  organizationIds: string[],
  enabled: boolean,
) => {
  const getAllowedTags = useGetAllowedOrganizationTagsHook();
  const shouldFetch = enabled && organizationIds.length > 0;

  const queries = useQueries({
    queries: organizationIds.map((organizationId) => ({
      queryKey: getGetAllowedOrganizationTagsQueryKey(organizationId),
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        getAllowedTags(organizationId, signal),
      enabled: shouldFetch,
    })),
  });

  const isLoading = shouldFetch && queries.some((query) => query.isPending);
  const isError = shouldFetch && queries.some((query) => query.isError);

  const allowedTags = useMemo(():
    | OrganizationAllowedBobcatTagDTO[][]
    | undefined => {
    if (!shouldFetch || isLoading) return undefined;
    if (queries.some((query) => query.data === undefined)) return undefined;
    return queries.map((query) => query.data ?? []);
  }, [isLoading, queries, shouldFetch]);

  return { allowedTags, isLoading, isError };
};
